// What may be sold at the pharmacy counter (docs/plans/phase1-dispensing.md §6.7, §2.4).
//
// Over-the-counter items sell to anyone. Prescription-only items need an outside
// prescription recorded against an identified patient, and the label instructions from
// it. Controlled drugs are never sold at the counter in Phase 1.
//
// TODO(verify-spec): plan §18 C1 and C2, accepted as defaults on 25 Sep 2026 pending the
// clinical safety lead: two supply classes only (prescription-only, over-the-counter), every
// item prescription-only until the pharmacist in charge reclassifies it; PPB's own classes
// and controlled-drug rules are not encoded until seen.

import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';

export const SUPPLY_CLASSES = ['prescription_only', 'over_the_counter'] as const;
export type SupplyClass = (typeof SUPPLY_CLASSES)[number];

export type SaleLineErrorCode =
  | 'sale.controlled_not_sellable'
  | 'sale.prescription_required'
  | 'sale.patient_required'
  | 'sale.instructions_required';

export const SALE_LINE_ERRORS: Record<SaleLineErrorCode, string> = {
  'sale.controlled_not_sellable':
    'Controlled drugs are dispensed only against a prescription from this facility',
  'sale.prescription_required':
    'This medicine is prescription-only. Record the outside prescription first',
  'sale.patient_required': 'A prescription-only sale needs the patient identified',
  'sale.instructions_required': 'Type the instructions from the prescription for the label',
};

export function checkSaleLine(input: {
  readonly supplyClass: SupplyClass;
  readonly isControlled: boolean;
  readonly hasExternalPrescription: boolean;
  readonly hasPatient: boolean;
  readonly instructions: string | null | undefined;
}): Result<SupplyClass, SaleLineErrorCode> {
  if (input.isControlled) return err('sale.controlled_not_sellable');
  if (input.supplyClass === 'over_the_counter') return ok('over_the_counter');
  if (!input.hasPatient) return err('sale.patient_required');
  if (!input.hasExternalPrescription) return err('sale.prescription_required');
  if ((input.instructions ?? '').trim() === '') return err('sale.instructions_required');
  return ok('prescription_only');
}

export type SaleTotalErrorCode = 'sale.not_fully_paid' | 'sale.overpaid';

/**
 * A counter sale is paid in full at the counter (plan §18 P2): the tenders must cover what
 * is owed exactly, with cash change given on top.
 */
export function checkSaleTotals(input: {
  readonly owedCents: bigint;
  readonly tenderedCents: bigint;
}): Result<bigint, SaleTotalErrorCode> {
  if (input.tenderedCents < input.owedCents) return err('sale.not_fully_paid');
  return ok(input.owedCents);
}
